import * as FNS from "date-fns";
import {
	compareDesc,
	format,
	formatDistance,
	isValid,
	differenceInMilliseconds,
	differenceInSeconds,
	differenceInMinutes,
	differenceInHours,
	differenceInDays,
	startOfDay,
	addMilliseconds,
	parseISO,
	differenceInCalendarDays,
	set,
} from "date-fns";
import {
	toZonedTime,
	formatInTimeZone,
	fromZonedTime,
	ToDateOptionsWithTZ,
} from "date-fns-tz";
import { ru, uk, enUS as en, az, tr, ro, ka, pl } from "date-fns/locale";
import { cloneDeep, isBoolean, isNumber, isString } from "lodash";
import { TFunction } from "i18next";

import { Language } from "../services";
import { ConstantLanguage } from "../assets/languages/langs";

export const DaysGuard = (day?: number): day is DateFns.DaysNumber =>
	typeof day === "number" && day >= 0 && day <= 6;

export const ConstantFormat = {
	BASE: "dd.MM.yyyy HH:mm:ss.SSSxxx",
	day: "eeee",
	month: "MMMM",
	year: "yyyy",

	MDYHMS: "MM.dd.yyyy hh:mm:ss",
	MDYHMSA: "MM.dd.yyyy hh:mm:ss aa",
	MDYHMA: "MM.dd.yyyy hh:mm aa",
	MDY: "MM.dd.yyyy",
	HMS: "hh:mm:ss",
	HMSA: "hh:mm:ss aa",
	HMA: "hh:mm aa",
	HA: "hh aa",
	DMMYHMSA: "dd MMMM yyyy hh:mm:ss aa",
	DMMYEHMSA: "eeee dd MMMM yyyy  hh:mm:ss aa",

	MDYHMS_UK: "dd.MM.yyyy HH:mm:ss",
	MDYHM_UK: "dd.MM.yyyy HH:mm",
	DMMYHMS_UK: "dd MMMM yyyy HH:mm:ss",
	DMMYEHMS_UK: "eeee dd MMMM yyyy  HH:mm:ss",
	MDY_UK: "dd.MM.yyyy",
	HMS_UK: "HH:mm:ss",
	HM_UK: "HH:mm",
	H_UK: "HH",
	K_UK: "k",
	KK_UK: "kk",

	EHMSA: "eeee hh:mm:ss aa",
	EHMS: "eeee HH:mm:ss",

	YOrder: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
	DMY_DASH: "dd-MM-yyyy",
	HM_DDMMYYYY_SLASH: "HH:mm dd/MM/yyyy",
	HMS_DDMMYYYY_SLASH: "HH:mm:ss dd/MM/yyyy",
} as const;

const ConstantTimeMs = {
	/** 00:00:00.000 */
	ms_0_00_00: 0,
	/** 00:01:00.000 */
	ms_00_01_00: 60_000,
	/** 00:05:00.000 */
	ms_00_05_00: 300_000,
	/** 00:10:00.000 */
	ms_00_10_00: 600_000,
	/** 00:15:00.000 */
	ms_00_15_00: 900_000,
	/** 00:30:00.000 */
	ms_00_30_00: 1_800_000,
	/** 01:00:00.000 */
	ms_01_00_00: 3_600_000,
	/** 02:00:00.000 */
	ms_02_00_00: 7_200_000,
	/** 03:00:00.000 */
	ms_03_00_00: 10_800_000,
	/** 04:00:00.000 */
	ms_04_00_00: 14_400_000,
	/** 05:00:00.000 */
	ms_05_00_00: 18_000_000,
	/** 06:00:00.000 */
	ms_06_00_00: 21_600_000,
	/** 07:00:00.000 */
	ms_07_00_00: 25_200_000,
	/** 08:00:00.000 */
	ms_08_00_00: 28_800_000,
	/** 09:00:00.000 */
	ms_09_00_00: 32_400_000,
	/** 10:00:00.000 */
	ms_10_00_00: 36_000_000,
	/** 11:00:00.000 */
	ms_11_00_00: 39_600_000,
	/** 12:00:00.000 */
	ms_12_00_00: 43_200_000,
	/** 13:00:00.000 */
	ms_13_00_00: 46_800_000,
	/** 14:00:00.000 */
	ms_14_00_00: 50_400_000,
	/** 15:00:00.000 */
	ms_15_00_00: 54_000_000,
	/** 16:00:00.000 */
	ms_16_00_00: 57_600_000,
	/** 17:00:00.000 */
	ms_17_00_00: 61_200_000,
	/** 18:00:00.000 */
	ms_18_00_00: 64_800_000,
	/** 19:00:00.000 */
	ms_19_00_00: 68_400_000,
	/** 20:00:00.000 */
	ms_20_00_00: 72_000_000,
	/** 21:00:00.000 */
	ms_21_00_00: 75_600_000,
	/** 22:00:00.000 */
	ms_22_00_00: 79_200_000,
	/** 23:00:00.000 */
	ms_23_00_00: 82_800_000,
	/** 23:59:59.999 */
	ms_23_59_59: 86_399_999,
	ONE_DAY: 86_400_000,
} as const;
export type TypeTimeMs = typeof ConstantTimeMs;
export type ValueTimeMs = (typeof ConstantTimeMs)[keyof typeof ConstantTimeMs];

// ! Not use this format (in key object) for date-fns
const BadMomentFormats: Record<string, keyof typeof ConstantFormat> = {
	"DD-MM-YYYY": "DMY_DASH",
	"DD.MM.YYYY": "MDY_UK",
	"MM.DD.YYYY": "MDY",
	"HH:mm DD/MM/yyyy": "HM_DDMMYYYY_SLASH",
	"HH:mm:ss DD/MM/yyyy": "HMS_DDMMYYYY_SLASH",
};

// https://www.npmjs.com/package/date-fns-tz
// https://date-fns.org/v2.23.0/docs/format
export class DateFns {
	private static instance: DateFns | null = null;

	public readonly timeMs: TypeTimeMs = ConstantTimeMs;

	/** time `24:00:00.000` */
	public readonly ONE_DAY_MS: number = ConstantTimeMs.ONE_DAY;

	/** time `23:59:59.999` */
	public readonly END_DAY = ConstantTimeMs.ms_23_59_59;

	public year: number = new Date().getFullYear();

	public month: number = new Date().getMonth();

	public locales: Map<Language, Locale> = new Map<Language, Locale>();

	public timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

	public localTimezone: string =
		Intl.DateTimeFormat().resolvedOptions().timeZone;

	public lang: Language = ConstantLanguage.UK;

	public fns = FNS;

	constructor(
		data: { lang?: Language; timezone?: string } = {
			lang: ConstantLanguage.UK,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		},
	) {
		if (DateFns.instance) {
			return DateFns.instance;
		}

		this.locales.set(ConstantLanguage.UK, uk);
		this.locales.set(ConstantLanguage.RU, ru);
		this.locales.set(ConstantLanguage.EN, en);
		this.locales.set(ConstantLanguage.AZ, az);
		this.locales.set(ConstantLanguage.TR, tr);
		this.locales.set(ConstantLanguage.RO, ro);
		this.locales.set(ConstantLanguage.KA, ka);
		this.locales.set(ConstantLanguage.PL, pl);

		this.lang = data.lang || ConstantLanguage.UK;
		this.timezone =
			data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
		this.fns = FNS;

		DateFns.instance = this;
	}

	// ------------------------------------------------

	public setTimeZone(timeZone: string): void {
		if (timeZone) this.timezone = timeZone;
	}

	public setLang(lang: Language) {
		this.lang = lang;
	}

	public getMonths = (): string[] => {
		const months = Array.from(Array(this.month), (_, i) =>
			format(new Date(new Date().getFullYear(), i + 1, i + 1), "MMMM"),
		);
		return months;
	};

	public getAllMonths = (): string[] => {
		const months = Array.from(Array(12), (_, i) =>
			format(new Date(new Date().getFullYear(), i + 1, i + 1), "MMMM"),
		);
		return months;
	};

	public getDay = (): number =>
		Number(format(new Date(), "dd MMMM yyyy").substring(0, 2));

	public getDaysInWeek = (): string[] =>
		Array.from(Array(7), (_, i) =>
			format(
				new Date(
					new Date().getFullYear(),
					new Date().getMonth(),
					i + 1,
				),
				"dd",
			),
		);

	public getDaysInMonth = (
		month: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
		year: number = new Date().getFullYear(),
		type: "eeee" | "eee" | "do" | "d" | "dd" = "dd",
	): string[] => {
		const f = new Date(year, month);
		const days = format(f, "dd");
		const getMonth: number = month || new Date().getMonth();
		return Array.from(Array(Number(days)), (_, i) =>
			format(new Date(year, getMonth, i + 1), type),
		);
	};

	public getDayName = (
		type: "eeee" | "eee" | "eeeeee" | "eo" | "e" | "ee" = "ee",
	): number => Number(format(new Date(), type).substring(0, 2));

	public getDayNameByNumber = (
		week: DateFns.DaysNumber | string = 0,
		type: "eeee" | "eee" | "eeeeee" | "eo" | "e" | "ee" = "eeee",
	): string => {
		const locale = this.locale(this.lang);
		const start = this.fns.startOfWeek(new Date(), {
			weekStartsOn: 1,
			locale,
		});
		if (isNumber(week)) {
			const day = this.fns.addDays(start, week);
			return format(day, type, { locale });
		}

		const numWeek = this.getDayNumber(week);
		if (!isNumber(numWeek)) return "";

		const day = this.fns.addDays(start, numWeek);

		return format(day, type, { locale });
	};

	public getDayNumber = (
		week:
			| string
			| "monday"
			| "tuesday"
			| "wednesday"
			| "thursday"
			| "friday"
			| "saturday"
			| "sunday",
	): DateFns.DaysNumber | undefined => {
		let numStr: DateFns.DaysNumber | undefined;
		if (week === "monday") numStr = 0;
		if (week === "tuesday") numStr = 1;
		if (week === "wednesday") numStr = 2;
		if (week === "thursday") numStr = 3;
		if (week === "friday") numStr = 4;
		if (week === "saturday") numStr = 5;
		if (week === "sunday") numStr = 6;
		return numStr;
	};

	public getDayByNumber = (num: number): string => {
		if (num === 0) return "monday";
		if (num === 1) return "tuesday";
		if (num === 2) return "wednesday";
		if (num === 3) return "thursday";
		if (num === 4) return "friday";
		if (num === 5) return "saturday";
		if (num === 6) return "sunday";
		return "";
	};

	private locale = (str?: Language): Locale | undefined => {
		if (!str) return undefined;
		return this.locales.get(str);
	};

	/** * Get Year, month, day, hour, min, second   */
	public format = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? "MM.dd.yyyy hh:mm"
				: "dd.MM.yyyy HH:mm:ss";
		return format(date, formatTime, { locale });
	};

	/** * Get Year, month, day. */
	public format2 = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? ConstantFormat.MDY
				: ConstantFormat.MDY_UK;
		return format(date, formatTime, { locale });
	};

	/** * Get hour, min, second */
	public format3 = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? ConstantFormat.HMSA
				: ConstantFormat.HMS_UK;
		return format(date, formatTime, { locale });
	};

	private formatHandleMoment = (str: string): DateFns.ValueFormat => {
		const key = BadMomentFormats[str as keyof typeof BadMomentFormats];
		if (key) {
			return ConstantFormat[key];
		}
		return str as DateFns.ValueFormat;
	};

	/**
	 * Please use `formatTime2` instead.
	 */
	public formatTime = (
		date: string | Date | number | null | undefined,
		options: {
			formatTime?: DateFns.ValueFormat;
			lang?: Language;
			/** "UTC" or  "Europe/Kyiv" or "Europe/Warsaw" ... */
			timezone?: string;
			isTimezone?: boolean;
			isLocalTime?: boolean;
			isZeroSeconds?: boolean;
		},
	): string => {
		if (date === null || date === undefined) return "";

		const {
			formatTime = ConstantFormat.BASE,
			lang = this.lang,
			timezone = this.timezone,
			isTimezone = false,
			isLocalTime = false,
			isZeroSeconds = false,
		} = options || {
			formatTime: ConstantFormat.BASE,
			lang: this.lang,
			timezone: this.timezone,
			isTimezone: false,
			isLocalTime: false,
			isZeroSeconds: false,
		};

		const value = cloneDeep(date);
		const locale = this.locale(lang);
		const ft = this.formatHandleMoment(formatTime);
		let parsedDate: Date;

		if (isString(value) || isNumber(value)) {
			parsedDate = new Date(value);
		} else {
			parsedDate = value;
		}

		if (!isValid(parsedDate)) return "";

		if (isTimezone) {
			parsedDate = toZonedTime(parsedDate, timezone);
		} else if (isLocalTime) {
			parsedDate = toZonedTime(parsedDate, this.localTimezone);
		}

		if (isZeroSeconds) {
			parsedDate = set(parsedDate, { seconds: 0, milliseconds: 0 });
		}

		return format(parsedDate, ft, { locale }) ?? "";
	};

	public formatTime2 = (
		date: string | Date | number | null | undefined,
		formatTime: DateFns.ValueFormat,
		options?: {
			lang?: Language; // ? to change language
			timezone?: string; // ? analog moment(...).tz(timezoneName) // priority zone than toLocalTime
			toLocalTime?: boolean; // ? analog moment(...).local(true)
			zeroSeconds?: boolean; // ? analog moment(...).set({ second: 0, millisecond: 0 })
		},
	): string => {
		const { lang, toLocalTime, timezone, zeroSeconds } = options || {};

		if (date === null || date === undefined) return "";

		const _lang = lang || this.lang;
		const _formatTime = this.formatHandleMoment(formatTime);
		const locale = this.locale(_lang);

		let parsedDate: Date;

		if (typeof date === "string" || typeof date === "number") {
			parsedDate = new Date(date);
		} else {
			parsedDate = date;
		}

		if (!isValid(parsedDate)) return "";

		let finalDate = parsedDate;

		if (timezone) {
			finalDate = toZonedTime(parsedDate, timezone);
		} else if (toLocalTime) {
			const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
			finalDate = toZonedTime(parsedDate, localTz);
		}

		if (zeroSeconds) {
			finalDate = set(finalDate, { seconds: 0, milliseconds: 0 });
		}

		return format(finalDate, _formatTime, { locale }) ?? "";
	};

	public now = (
		{
			timezone = "",
			toLocalTime = false,
		}: { timezone?: string; toLocalTime?: boolean } = {
			timezone: "",
			toLocalTime: false,
		},
	): number =>
		toLocalTime
			? Date.now()
			: this.fromZonedTime(
					new Date(),
					timezone || this.timezone,
			  ).getTime();

	public fromZonedTime = (
		date: string | number | Date,
		timezone?: string,
		options?: ToDateOptionsWithTZ,
	) =>
		fromZonedTime(
			formatInTimeZone(
				date,
				timezone || this.timezone,
				"yyyy-MM-dd 00:00:00",
			),
			timezone || this.timezone,
			options,
		);

	public toZonedTime = (
		date: string | number | Date,
		timezone?: string,
		options?: ToDateOptionsWithTZ,
	) => toZonedTime(new Date(date), timezone || this.timezone, options);

	public setTime = (
		date: number | Date,
		options: {
			year?: number | undefined;
			month?: number | undefined;
			date?: number | undefined;
			hours?: number | undefined;
			minutes?: number | undefined;
			seconds?: number | undefined;
			milliseconds?: number | undefined;
		},
	) => set(date, options);

	/**
	 *
	 * * time - Date or ISO string
	 * * timezone - target IANA timezone (e.g. "Europe/Kyiv","Europe/Warsaw", "America/Noronha")
	 */
	public replaceTimezoneKeepLocal = (time: string, timezone?: string) => {
		const serviceTimezone = this.getTimezoneOffsetTime({
			timezone: timezone || this.timezone,
		});
		const sign = serviceTimezone >= 0 ? "+" : "-";
		const hours = String(Math.abs(serviceTimezone)).padStart(2, "0");
		const offset = `${sign}${hours}:00`;
		return time.replace(/([+-]\d{2}:\d{2})$/, offset);
	};

	/**
	 * Like `replaceTimezoneKeepLocal`, but the offset is the one `timezone` has
	 * on that date, not today: a preorder for tomorrow after a DST switch.
	 *
	 * * time - ISO string, its wall-clock time is kept
	 * * timezone - target IANA timezone (e.g. "Europe/Kyiv")
	 */
	public replaceTimezoneKeepLocalAt = (time: string, timezone?: string) => {
		const tz = timezone || this.timezone;
		const wallClock = time.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
		const instant = fromZonedTime(wallClock, tz);
		// The repeated hour of an autumn switch: take its first pass, as the browser does.
		const firstPass = new Date(
			instant.getTime() - ConstantTimeMs.ms_01_00_00,
		);
		const sameWallClock =
			formatInTimeZone(firstPass, tz, "yyyy-MM-dd'T'HH:mm:ss.SSS") ===
			formatInTimeZone(instant, tz, "yyyy-MM-dd'T'HH:mm:ss.SSS");
		return formatInTimeZone(
			sameWallClock ? firstPass : instant,
			tz,
			ConstantFormat.YOrder,
		);
	};

	public getTimezoneOffsetTime = (
		opt: {
			timezone?: string;
			m?: boolean;
			s?: boolean;
			ms?: boolean;
			abs?: boolean;
		} = { timezone: this.timezone },
	): number => {
		const {
			timezone = this.timezone,
			m = false,
			s = false,
			ms = false,
			abs = false,
		} = opt;

		const date = new Date();
		const dtf = new Intl.DateTimeFormat("en-US", {
			timeZone: timezone || this.timezone,
			hour12: false,
			hour: "numeric",
			minute: "numeric",
		});
		// Форматируем текущее время в нужной TZ
		const [{ value: hour }] = dtf
			.formatToParts(date)
			.filter((p) => p.type === "hour");

		// Получаем разницу относительно UTC
		const utcHour = date.getUTCHours();
		let offset = Number(hour) - utcHour;

		// Если offset меньше -12 или больше 12, корректируем (летнее время)
		if (offset < -12) offset += 24;
		if (offset > 12) offset -= 24;
		if (ms) return abs ? Math.abs(offset * 3_600_000) : offset * 3_600_000;
		if (s) return abs ? Math.abs(offset * 3_600) : offset * 3_600;
		if (m) return abs ? Math.abs(offset * 60) : offset * 60;
		return abs ? Math.abs(offset) : offset;
	};

	public formatTimeFromMilliseconds = (
		date: number,
		formatTime: DateFns.ValueFormat,
		lang: Language = this.lang,
	): string => {
		const locale = this.locale(lang);
		const time = new Date(date);
		if (!isValid(time)) return "";
		return format(time, formatTime, { locale });
	};

	/**
	 * Getting the remaining time
	 *
	 * @param {(number | Date)} start
	 * @param {(number | Date)} end
	 * @param {?Language} [locale]
	 * @returns {string}
	 */
	public formatDistance = (
		start: number | Date,
		end: number | Date,
		locale?: Language,
	) => {
		const time = formatDistance(start, end, {
			locale: this.locale(locale),
		});
		return time;
	};

	/** * https://date-fns.org/v2.30.0/docs/compareDesc
	 ** now === to = 0
	 ** now < to  = 1
	 ** now > to  = -1
	 */
	public compareDesc = (
		from: number | Date,
		to: number | Date,
	): 0 | 1 | -1 => {
		const value = compareDesc(from, to);
		if (value === 1) return 1;
		if (value === 0) return 0;
		return -1;
	};

	/** * Sorts the array by the specified time */
	public isPeriod = (
		date: string | Date | null | undefined,
		period?: DateFns.Period,
	): boolean => {
		if (date === null || date === undefined) return false;
		const newDate = new Date(date);

		if (period?.from && period?.to) {
			const dateFrom = new Date(period.from);
			const dateTo = new Date(period.to);
			const isValidTo = this.compareDesc(dateFrom, newDate);
			const isValidFrom = this.compareDesc(newDate, dateTo);
			return isValidTo !== -1 && isValidFrom !== -1;
		}
		if (period?.from) {
			const from = new Date(period.from);
			const isValid = this.compareDesc(from, newDate);
			return isValid !== -1;
		}
		if (period?.to) {
			const to = new Date(period.to);
			const isValid = this.compareDesc(newDate, to);
			return isValid !== -1;
		}

		return true;
	};

	/**
	 * Formats a date string to a human-readable format.
	 * @param dateString - The date string to format.
	 * @param badValue - The value to return if the date string is invalid.
	 * @returns The formatted date string or the badValue if invalid.
	 */

	public getCurrentPeriod = (
		startDate: number | Date,
		periodDays: number,
	): {
		from: number;
		to: number;
	} => {
		const now = new Date().getTime();
		const startDateMs = new Date(startDate).getTime();

		const differenceDays = (now - startDateMs) / this.ONE_DAY_MS;
		const periodsFloor =
			Math.floor(differenceDays / periodDays) * periodDays;
		const periodsCeil = Math.ceil(differenceDays / periodDays) * periodDays;
		const from = startDateMs + periodsFloor * this.ONE_DAY_MS;
		const to = startDateMs + periodsCeil * this.ONE_DAY_MS;

		return { from, to };
	};

	public millisecondsToDate = (
		timestamp: number,
		options: {
			/** timezone + or - */
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus, utc = false } = options;
		const newDate = startOfDay(new Date());

		const timezone = this.getTimezoneOffsetTime({
			ms: true,
			abs: true,
		});

		const timePlus =
			!utc && isBoolean(plus) && plus ? timestamp + timezone : timestamp;

		const time = addMilliseconds(newDate, timePlus);

		if (utc) {
			const value = plus
				? time.getTime() + timezone
				: time.getTime() - timezone;

			const utcTime = new Date(value);
			return utcTime;
		}

		return time;
	};

	public millisecondsFromDate = (
		date: Date,
		options: {
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus = true, utc = false } = options;
		let givenDate = date;
		let timezone: number | undefined;

		if (utc) {
			timezone = this.getTimezoneOffsetTime({
				ms: true,
				abs: true,
			});
			const value = plus
				? date.getTime() + timezone
				: date.getTime() - timezone;
			givenDate = new Date(value);
		}

		const startOfGivenDay = startOfDay(givenDate);
		const milliseconds = differenceInMilliseconds(
			givenDate,
			startOfGivenDay,
		);

		if (!utc && !plus) {
			if (timezone === undefined) {
				timezone = this.getTimezoneOffsetTime({
					ms: true,
					abs: true,
				});
			}
			return milliseconds - timezone;
		}

		return milliseconds;
	};

	public millisecondsFromDay = (
		date: Date,
		options: {
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus = true, utc = false } = options;
		let givenDate = date;
		let timezone: number | undefined;

		if (utc) {
			timezone = this.getTimezoneOffsetTime({
				ms: true,
				abs: true,
			});
			const value = plus
				? date.getTime() + timezone
				: date.getTime() - timezone;
			givenDate = new Date(value);
		}

		const startOfGivenDay = startOfDay(givenDate);
		const milliseconds = differenceInMilliseconds(
			givenDate,
			startOfGivenDay,
		);

		const ms = milliseconds >= 0 ? milliseconds : 0;
		return Math.min(ms, ConstantTimeMs.ms_23_59_59);
	};

	public millisecondsToDay = (
		timestamp: number,
		options: {
			/** timezone + or - */
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus, utc = false } = options;

		const time = Math.min(
			Math.max(timestamp, 0),
			ConstantTimeMs.ms_23_59_59,
		);

		const baseDay = utc ? this.startDate() : this.startOfDay();

		const startOfDay = this.setTime(baseDay, {
			month: 0,
			date: 0,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
		});
		// Добавляем часы в миллисекундах
		let result = addMilliseconds(startOfDay, time);

		// Если локально и указан plus, добавляем/вычитаем таймзону
		if (!utc && typeof plus === "boolean") {
			const timezone = this.getTimezoneOffsetTime({ ms: true });
			result = addMilliseconds(result, plus ? timezone : -timezone);
		}

		return result;
	};

	public getUTCTime = (
		date: Date | number | string,
		options: {
			ms?: boolean;
			plus?: boolean;
			utc?: boolean;
			iso?: boolean;
			isFns?: boolean;
		},
	): number | Date | string => {
		const { ms = false, plus = true, iso = false, isFns = false } = options;
		const dateTime = new Date(date).getTime();
		const zone = this.getTimezone();

		if (isFns) return format(dateTime, ConstantFormat.YOrder);
		if (plus) {
			const time = dateTime + zone;

			if (ms) return time;
			if (iso) return new Date(time).toISOString();

			return new Date(time);
		}

		const time = dateTime - zone;

		if (ms) return time;
		if (iso) return new Date(time).toISOString();

		return new Date(time);
	};

	public getTimezone = (): number => {
		const time = Math.abs(new Date().getTimezoneOffset() * 60_000);
		return time;
	};

	public startOfDay(date: Date = new Date()) {
		return this.fns.startOfDay(new Date(date));
	}

	public endOfDay(date: Date = new Date()) {
		return this.fns.endOfDay(new Date(date));
	}

	public getMillisecondsDiff = (
		start: Date | number,
		end: Date | number,
	): number => {
		const s = new Date(start);
		const e = new Date(end);

		if (!isValid(s) || !isValid(e)) return 0;
		return differenceInMilliseconds(s, e);
	};

	public getDiff = (
		from: Date | string | number,
		to: Date | string | number = this.now(),
		unit:
			| "milliseconds"
			| "seconds"
			| "minutes"
			| "hours"
			| "days" = "milliseconds",
	): number => {
		const date1 = new Date(from);
		const date2 = new Date(to);

		if (!isValid(date1) || !isValid(date2)) return 0;

		switch (unit) {
			case "seconds":
				return differenceInSeconds(date1, date2);
			case "minutes":
				return differenceInMinutes(date1, date2);
			case "hours":
				return differenceInHours(date1, date2);
			case "days":
				return differenceInDays(date1, date2);
			default:
				return differenceInMilliseconds(date1, date2);
		}
	};

	/**
	 * Converts a date string to time in hours and minutes format.
	 * @param dateString - The date string to convert.
	 * @returns The time in hours and minutes (e.g., "15:30") or empty string if invalid.
	 */
	public toClockFaceTime = (
		dateString: string | null | undefined,
		badValue: string | null | undefined = "",
	): string | null => {
		if (!dateString) {
			return badValue;
		}

		const date = new Date(dateString);

		if (!isValid(date)) {
			return badValue;
		}

		return format(date, ConstantFormat.HM_UK);
	};

	/**
	 * Formats a date string to a human-readable format similar to MomentFnc.toDateNavigator.
	 * @param args - The arguments object containing the date string and the bad value.
	 * @returns The formatted date string or the badValue if invalid.
	 */
	public toDateNavigator = ({
		t,
		dateToFormat,
		badValue = "",
		formatVersion = "",
	}: DateFns.ToDateNavigator): string | null => {
		if (!dateToFormat) {
			return badValue;
		}

		const date = parseISO(dateToFormat);

		if (!isValid(date)) {
			return badValue;
		}

		const today = startOfDay(new Date());
		const targetDay = startOfDay(date);
		const diffInDays = differenceInCalendarDays(targetDay, today);

		let formatFinish: string | undefined;

		const todayT = t("dates.today") ?? "Today";
		const yesterdayT = t("dates.yesterday") ?? "Yesterday";
		const tomorrowT = t("dates.tomorrow") ?? "Tomorrow";

		if (formatVersion === "format1") {
			// diffInDays === 0 — сегодня
			// diffInDays === 1 — завтра (tomorrow)
			// diffInDays === -1 — вчера (yesterday)
			formatFinish = new Map<number, string>([
				[0, todayT],
				[1, tomorrowT],
				[-1, yesterdayT],
			]).get(diffInDays);
		}

		if (formatVersion === "format2") {
			formatFinish = diffInDays === 0 ? "" : undefined;
		}

		const retval = formatFinish ?? format(date, ConstantFormat.MDY_UK);

		return retval;
	};

	public startDate = (
		opt: {
			isToZoneTime?: boolean;
			timezone?: string;
		} = {},
	) => {
		const { isToZoneTime = true } = opt;
		const time = this.fns.startOfDay(new Date());
		const timestamp = this.replaceTimezoneKeepLocal(
			this.formatTime(time, {
				isTimezone: false,
				formatTime: ConstantFormat.YOrder,
			}),
		);
		if (isToZoneTime) {
			const startDateValue = new Date(timestamp);
			return this.toZonedTime(`${startDateValue}`, opt?.timezone);
		}

		return new Date(timestamp);
	};

	public endDate(opt: { isToZoneTime?: boolean; timezone?: string } = {}) {
		const { isToZoneTime = true } = opt;
		const time = this.fns.endOfDay(new Date());
		const timestamp = this.replaceTimezoneKeepLocal(
			this.formatTime(time, {
				isTimezone: false,
				formatTime: ConstantFormat.YOrder,
			}),
		);
		if (isToZoneTime) {
			const startDateValue = new Date(timestamp);
			return this.toZonedTime(`${startDateValue}`, opt?.timezone);
		}
		return new Date(timestamp);
	}
}

export declare namespace DateFns {
	type Period = {
		from?: Date;
		to?: Date;
	};

	type TypeFormat = typeof ConstantFormat;

	type ValueFormat = (typeof ConstantFormat)[keyof typeof ConstantFormat];

	type DaysNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;

	interface ToDateNavigator {
		t: TFunction;
		dateToFormat: string | null | undefined;
		badValue?: string | null | undefined;
		formatVersion?: string | null | undefined;
	}
}

/**
 * Moment.js → Date-fns форматирование дат
 * ----------------------------------------
 * Используй эту таблицу для быстрой замены форматов при миграции с moment.js
 * Обрати внимание: date-fns чувствителен к регистру символов!
 *
 * ┌──────────────────────┬────────────────────────────┐
 * │     Moment.js        │        date-fns            │
 * ├──────────────────────┼────────────────────────────┤
 * │ YYYY (год)           │ yyyy                       │
 * │ YY (2 цифры года)    │ yy                         │
 * │ M (месяц)            │ M                          │
 * │ MM (месяц с 0)       │ MM                         │
 * │ MMM (сокр. месяц)    │ MMM                        │
 * │ MMMM (полный месяц)  │ MMMM                       │
 * │ D (день)             │ d                          │
 * │ DD (день с 0)        │ dd                         │
 * │ d (день недели от 0) │ i (1–7, ISO, понедельник)  │
 * │ E (день недели)      │ e (0–6, местный, вс – 0)    │
 * │ H (часы 0–23)        │ H                          │
 * │ HH (часы с 0)        │ HH                         │
 * │ h (часы 1–12)        │ h                          │
 * │ hh (часы 1–12 с 0)   │ hh                         │
 * │ m (минуты)           │ m                          │
 * │ mm (минуты с 0)      │ mm                         │
 * │ s (секунды)          │ s                          │
 * │ ss (секунды с 0)     │ ss                         │
 * │ a (am/pm)            │ aa                         │
 * │ A (AM/PM)            │ aa                         │
 * │ Z (зона смещения)    │ XXX                        │
 * │ ZZ (зона числом)     │ xx                         │
 * │ X (timestamp в сек)  │ t                          │
 * │ x (timestamp в мс)   │ T                          │
 * └──────────────────────┴────────────────────────────┘
 *
 * Пример:
 * moment:     moment(date).format("DD.MM.YYYY HH:mm:ss")
 * date-fns:   format(date, "dd.MM.yyyy HH:mm:ss")
 */
